import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfDay, subMonths, eachDayOfInterval, endOfDay } from "date-fns";
import { getAllMetrics } from "@/lib/metricDefinitions";

interface MiniGraphOverlayProps {
  userId: string;
  metricKey: string;
  metricEmoji?: string;
  metricLabel?: string;
  snapDate: Date;
  lightText?: boolean;
}

interface DailyEntry {
  entry_date: string;
  [key: string]: any;
}

interface CustomMetric {
  metric_key: string;
  metric_name: string;
  metric_emoji: string;
  metric_unit: string;
  metric_type?: "numeric" | "boolean";
  is_inverse?: boolean;
}

const defaultColors = {
  0: "rgb(229 231 235)", // gray-200
  1: "rgb(134 239 172)", // green-300
  2: "rgb(74 222 128)", // green-400
  3: "rgb(34 197 94)", // green-500
  4: "rgb(22 163 74)", // green-600
};

const redColors = {
  "-1": "rgb(254 202 202)", // red-200
  "-2": "rgb(252 165 165)", // red-300
  "-3": "rgb(248 113 113)", // red-400
  "-4": "rgb(239 68 68)", // red-500
};

const getIntensity = (value: number | null, goal: number): number => {
  if (!value) return 0;
  const percentage = (value / goal) * 100;
  if (percentage >= 100) return 4;
  if (percentage >= 75) return 3;
  if (percentage >= 50) return 2;
  if (percentage > 0) return 1;
  return 0;
};

const getCalorieIntensity = (value: number | null, goal: number, isInverse: boolean = true): number => {
  if (!value) return 0;
  const percentage = (value / goal) * 100;
  
  if (isInverse) {
    // Cutting mode: over goal is bad (red)
    if (percentage > 100) {
      const overshoot = percentage - 100;
      if (overshoot >= 50) return -4;
      if (overshoot >= 25) return -3;
      if (overshoot >= 10) return -2;
      return -1;
    }
    if (percentage >= 100) return 4;
    if (percentage >= 75) return 3;
    if (percentage >= 50) return 2;
    if (percentage > 0) return 1;
    return 0;
  } else {
    // Bulking mode: no red, just normal green shading based on progress
    if (percentage >= 100) return 4;
    if (percentage >= 75) return 3;
    if (percentage >= 50) return 2;
    if (percentage > 0) return 1;
    return 0;
  }
};

const getBooleanIntensity = (value: number | null, isInverse: boolean): number => {
  if (!value) return 0;
  return isInverse ? -4 : 4;
};

export const MiniGraphOverlay = ({
  userId,
  metricKey,
  metricEmoji,
  metricLabel,
  snapDate,
  lightText = false,
}: MiniGraphOverlayProps) => {
  // Calculate date range (3 months back from snap date)
  const endDate = endOfDay(snapDate);
  const startDate = startOfDay(subMonths(snapDate, 3));
  
  // Fetch daily entries for 3 month period
  const { data: entries = [] } = useQuery({
    queryKey: ["mini-graph", userId, metricKey, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", userId)
        .gte("entry_date", format(startDate, "yyyy-MM-dd"))
        .lte("entry_date", format(endDate, "yyyy-MM-dd"))
        .order("entry_date", { ascending: true });
      
      if (error) throw error;
      return data as DailyEntry[];
    },
    enabled: !!userId,
  });

  // Fetch custom metric details if needed
  const { data: customMetric } = useQuery({
    queryKey: ["custom-metric", userId, metricKey],
    queryFn: async () => {
      if (!metricKey.startsWith('custom_')) return null;
      
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", userId)
        .eq("metric_key", metricKey)
        .maybeSingle();
      
      if (error) throw error;
      return data as CustomMetric;
    },
    enabled: metricKey.startsWith('custom_'),
  });

  // Fetch user goals
  const { data: goals } = useQuery({
    queryKey: ["user-goals", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Fetch chart preferences for color
  const { data: chartPreferences } = useQuery({
    queryKey: ["chart-preferences", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_chart_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Create entry lookup
  const entryMap = new Map(entries.map(e => [e.entry_date, e]));
  
  // Generate all days in range
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Organize into weeks
  const weeks: Array<Array<{ date: Date; entry: DailyEntry | null }>> = [];
  let currentWeek: Array<{ date: Date; entry: DailyEntry | null }> = [];
  
  allDays.forEach((date) => {
    const dayOfWeek = date.getDay();
    
    if (dayOfWeek === 0 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    
    const dateStr = format(date, "yyyy-MM-dd");
    currentWeek.push({ date, entry: entryMap.get(dateStr) || null });
  });
  
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  // Get metric info (using useQuery since getAllMetrics is async)
  const { data: allMetrics = [] } = useQuery({
    queryKey: ["all-metrics"],
    queryFn: getAllMetrics,
  });
  
  const metricInfo = allMetrics.find(m => m.key === metricKey);
  const displayLabel = customMetric?.metric_name || metricLabel || metricInfo?.label || metricKey;
  const displayEmoji = customMetric?.metric_emoji || metricEmoji || metricInfo?.emoji || "";
  
  // Get goal value
  const goalKey = `${metricKey}_goal` as keyof typeof goals;
  const goalValue = goals?.[goalKey] as number || metricInfo?.defaultGoal || 100;
  
  // Determine metric type
  const isBoolean = customMetric?.metric_type === "boolean" || 
                    metricKey === "ate_healthy" || 
                    metricKey === "low_sugars" || 
                    metricKey === "fasted";
  // For calories, use calories_inverse from goals; for added_sugars always true; for custom metrics use is_inverse
  const isInverse = metricKey === "calories" 
    ? (goals?.calories_inverse !== false) 
    : (customMetric?.is_inverse || metricKey === "added_sugars");
  
  // Get custom colors
  const colorPreferences = chartPreferences?.metric_colors?.[metricKey];
  
  // Helper to get value from entry
  const getValueFromEntry = (entry: DailyEntry | null): number | null => {
    if (!entry) return null;
    const valueKey = metricKey.endsWith('_value') ? metricKey : `${metricKey}`;
    return entry[valueKey] ?? entry[`${metricKey}_value`] ?? null;
  };

  // Helper to calculate intensity
  const calculateIntensity = (entry: DailyEntry | null): number => {
    const value = getValueFromEntry(entry);
    
    if (isBoolean) {
      return getBooleanIntensity(value, isInverse);
    }
    
    if (isInverse) {
      return getCalorieIntensity(value, goalValue, isInverse);
    }
    
    return getIntensity(value, goalValue);
  };

  // Helper to get square color
  const getSquareColor = (intensity: number): string => {
    const customColor = colorPreferences?.[intensity.toString()];
    if (customColor) return customColor;
    
    if (intensity < 0) {
      return redColors[intensity.toString() as keyof typeof redColors] || redColors["-1"];
    }
    
    return defaultColors[intensity as keyof typeof defaultColors] || defaultColors[0];
  };

  // Get month labels for top
  const getMonthLabel = (weekIndex: number): string => {
    const firstDayOfWeek = weeks[weekIndex]?.[0]?.date;
    if (!firstDayOfWeek) return "";
    
    const prevWeekFirstDay = weekIndex > 0 ? weeks[weekIndex - 1]?.[0]?.date : null;
    
    if (!prevWeekFirstDay || firstDayOfWeek.getMonth() !== prevWeekFirstDay.getMonth()) {
      return format(firstDayOfWeek, "MMM");
    }
    
    return "";
  };

  // Calculate range (min to goal)
  const minValue = 0;
  const maxValue = goalValue;

  const textColorClass = lightText ? "text-white/90" : "text-muted-foreground/80";
  
  return (
    <div className="w-full px-3 py-2" style={{ opacity: 0.5 }}>
      <div className="space-y-1.5">
        {/* Graph squares */}
        <div className="flex gap-[1.5px]">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-[1.5px]">
              {week.map((day, dayIndex) => {
                const intensity = calculateIntensity(day.entry);
                const bgColor = getSquareColor(intensity);
                
                // Check if this is the snap date
                const isSnapDate = format(day.date, "yyyy-MM-dd") === format(snapDate, "yyyy-MM-dd");
                
                return (
                  <div
                    key={dayIndex}
                    className="w-1.5 h-1.5 rounded-full relative"
                    style={{ backgroundColor: bgColor }}
                  >
                    {isSnapDate && (
                      <div 
                        className="absolute inset-0 rounded-full border-[0.5px]"
                        style={{ 
                          borderColor: lightText ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                          boxShadow: lightText 
                            ? '0 0 0 0.5px rgba(255, 255, 255, 0.3)' 
                            : '0 0 0 0.5px rgba(0, 0, 0, 0.2)'
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        
        {/* Month labels */}
        <div className="flex gap-[1.5px]">
          {weeks.map((_, weekIndex) => (
            <div key={weekIndex} className={`text-[6px] ${textColorClass} text-center`} style={{ width: '6px' }}>
              {getMonthLabel(weekIndex)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
